import { deviceLocal } from '@/privacy/deviceStorage';
/**
 * The invitation link a person opened and has not joined yet, kept across sign-in or sign-up.
 *
 * A module of its own because `AuthStore` forgets it on sign-out, so whatever this file imports is in
 * the launch entry: kept apart from `invitationModel` (the invite page's and the panels' arithmetic,
 * and the seeded role catalogue it reads), which loads with those screens (gate 707, T-0559).
 */

/** Where a pending link is kept across sign-in or sign-up, on this device only. */
export const PENDING_INVITATION_KEY = 'featit:pendingInvitation';

export function rememberPendingInvitation(token: string): void {
  try {
    deviceLocal.setItem(PENDING_INVITATION_KEY, token);
  } catch {
    // A browser that refuses storage still redeems in the same page load; it only cannot resume.
  }
}

/** D-0418: the place an individual link named, kept with the pending link. */
export const PENDING_INVITATION_PLACE_KEY = 'featit:pendingInvitationPlace';

export function rememberPendingInvitationPlace(place: string | null): void {
  try {
    if (place) deviceLocal.setItem(PENDING_INVITATION_PLACE_KEY, place);
    else deviceLocal.removeItem(PENDING_INVITATION_PLACE_KEY);
  } catch {
    // As the link itself: a browser that refuses storage cannot resume.
  }
}

export function pendingInvitationPlace(): string | null {
  try {
    return deviceLocal.getItem(PENDING_INVITATION_PLACE_KEY);
  } catch {
    return null;
  }
}

export function pendingInvitation(): string | null {
  try {
    return deviceLocal.getItem(PENDING_INVITATION_KEY);
  } catch {
    return null;
  }
}

/**
 * F-0809 (EAI-1, ADR-208): a System Admin's invitation to the deployment (`/-/join#<token>`), kept as a
 * place invitation's link is: on this device, through the same seam, until it is spent or refused,
 * and forgotten by `forgetPendingInvitation` with the rest, on signing out too.
 */
export const PENDING_DEPLOYMENT_INVITATION_KEY = 'featit:pendingDeploymentInvitation';

export function rememberPendingDeploymentInvitation(token: string): void {
  try {
    deviceLocal.setItem(PENDING_DEPLOYMENT_INVITATION_KEY, token);
  } catch {
    // A browser that refuses storage still signs up in the same page load; it only cannot resume.
  }
}

export function pendingDeploymentInvitation(): string | null {
  try {
    return deviceLocal.getItem(PENDING_DEPLOYMENT_INVITATION_KEY);
  } catch {
    return null;
  }
}

/** A refused deployment link only: a place invitation kept beside it stays. */
export function forgetPendingDeploymentInvitation(): void {
  try {
    deviceLocal.removeItem(PENDING_DEPLOYMENT_INVITATION_KEY);
  } catch {
    // Nothing kept, nothing to forget.
  }
}

export function forgetPendingInvitation(): void {
  try {
    deviceLocal.removeItem(PENDING_INVITATION_KEY);
    deviceLocal.removeItem(PENDING_DEPLOYMENT_INVITATION_KEY);
    deviceLocal.removeItem(PENDING_INVITATION_PLACE_KEY);
    deviceLocal.removeItem(SIGN_UP_INVITATION_KEY);
    deviceLocal.removeItem(SIGN_UP_INVITATION_PLACE_KEY);
  } catch {
    // Nothing to forget.
  }
}

/**
 * F-0584 MWI-28 (I-2170): the link an account was created from on this device. The sign-up spent its
 * use and the address's confirmation wrote its grants, so there is nothing left to Join: the first
 * sign-in after it asks the link's answer once, says what was joined and opens the first place
 * (`signUpInvitationLanding`). Kept with the pending link, and forgotten with it.
 */
export const SIGN_UP_INVITATION_KEY = 'featit:signUpInvitation';

export function rememberSignUpInvitation(token: string, place?: string | null): void {
  try {
    deviceLocal.setItem(SIGN_UP_INVITATION_KEY, token);
    // D-0418 LK-8: the landing asks for the same place the sign-up was made for, never the rest.
    if (place) deviceLocal.setItem(SIGN_UP_INVITATION_PLACE_KEY, place);
    else deviceLocal.removeItem(SIGN_UP_INVITATION_PLACE_KEY);
  } catch {
    // A browser that refuses storage lands as any first sign-in does.
  }
}

export const SIGN_UP_INVITATION_PLACE_KEY = 'featit:signUpInvitationPlace';

export function signUpInvitationPlace(): string | null {
  try {
    return deviceLocal.getItem(SIGN_UP_INVITATION_PLACE_KEY);
  } catch {
    return null;
  }
}

export function signUpInvitation(): string | null {
  try {
    return deviceLocal.getItem(SIGN_UP_INVITATION_KEY);
  } catch {
    return null;
  }
}
